/**
 * OK16-09：与后端 validation.ts fixtureTypeSchema 对齐，供 render 发送前规范化。
 */

export const FIXTURE_TYPES = [
  'pendant',
  'table_lamp',
  'floor_lamp',
  'spotlight',
  'downlight',
  'recessed',
  'wall_lamp',
  'chandelier',
  'window',
  'window_light',
  'sunlight',
  'natural_light',
  'ambient_light',
  'candle',
  'cove_light',
  'cove_lighting',
  'other',
] as const;

export type FixtureTypeEnum = (typeof FIXTURE_TYPES)[number];

const FIXTURE_TYPE_SET = new Set<string>(FIXTURE_TYPES);

/** 与后端 base64ImageSchema 一致 */
export const SPATIAL_DATA_URL_RE =
  /^data:image\/(png|jpe?g|pjpeg|webp|gif|avif|bmp|x-ms-bmp|tif|tiff|heic|heif);base64,/i;

const TYPE_ALIASES: Record<string, FixtureTypeEnum> = {
  cove: 'cove_light',
  cove_lighting: 'cove_lighting',
  recessed_light: 'recessed',
  down_light: 'downlight',
  down_lighting: 'downlight',
  wall_light: 'wall_lamp',
  ceiling_light: 'pendant',
  strip_light: 'cove_light',
  led_strip: 'cove_light',
  lamp: 'table_lamp',
  light: 'other',
};

export function isValidSpatialDataUrl(value: string): boolean {
  return SPATIAL_DATA_URL_RE.test(String(value || '').trim());
}

export function normalizeFixtureType(raw: unknown): FixtureTypeEnum {
  const s = String(raw ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');
  if (FIXTURE_TYPE_SET.has(s)) return s as FixtureTypeEnum;
  if (TYPE_ALIASES[s]) return TYPE_ALIASES[s];
  console.warn('[fixtureNormalize] 未知灯具 type，映射为 other:', raw);
  return 'other';
}

export function normalizeFixtureLabel(label: unknown, type: string): string {
  const t = String(label ?? '').trim();
  if (t.length >= 1) return t.slice(0, 100);
  const typeStr = String(type || '').trim();
  if (typeStr.length >= 1) return typeStr.slice(0, 100);
  return 'Light';
}

/** 发往 POST /api/gemini/render 的单个 fixture 字段规范化 */
export function normalizeFixtureForRender(fixture: Record<string, unknown>): Record<string, unknown> {
  const type = normalizeFixtureType(fixture.type);
  const label = normalizeFixtureLabel(fixture.label, type);
  const out: Record<string, unknown> = {
    ...fixture,
    type,
    label,
    isModified: typeof fixture.isModified === 'boolean' ? fixture.isModified : Boolean(fixture.isModified),
  };
  const x = Number(fixture.x);
  const y = Number(fixture.y);
  if (Number.isFinite(x)) out.x = Math.max(0, Math.min(1000, x));
  if (Number.isFinite(y)) out.y = Math.max(0, Math.min(1000, y));
  const intensity = Number(fixture.intensity);
  if (Number.isFinite(intensity)) out.intensity = Math.max(0, Math.min(2, intensity));
  const colorTemp = Number(fixture.colorTemp);
  if (Number.isFinite(colorTemp)) out.colorTemp = Math.max(-100, Math.min(100, colorTemp));
  return out;
}

export async function ensureSpatialMapDataUrl(
  value: string | undefined | null,
  fetchUrl: (url: string) => Promise<{ dataUrl?: string } | unknown>,
): Promise<string | undefined> {
  const s = String(value ?? '').trim();
  if (!s) return undefined;
  if (isValidSpatialDataUrl(s)) return s;
  if (/^https?:\/\//i.test(s)) {
    try {
      const resp = (await fetchUrl(s)) as { dataUrl?: string };
      const dataUrl = resp?.dataUrl;
      if (dataUrl && isValidSpatialDataUrl(dataUrl)) return dataUrl;
      console.warn('[fixtureNormalize] depth/normal HTTPS 转 data URL 失败，省略该字段:', s.slice(0, 80));
    } catch (e) {
      console.warn('[fixtureNormalize] depth/normal 拉取失败，省略该字段:', e);
    }
    return undefined;
  }
  console.warn('[fixtureNormalize] depth/normal 格式无效，省略:', s.slice(0, 60));
  return undefined;
}

/** 识别完成后写入 state 的深度/法线图（单张） */
export async function ensureSpatialMapDataUrlOrNull(
  value: string | undefined | null,
  fetchUrl: (url: string) => Promise<{ dataUrl?: string } | unknown>,
): Promise<string | null> {
  const out = await ensureSpatialMapDataUrl(value, fetchUrl);
  return out ?? null;
}
